import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layers, ArrowRight, Search, Calculator, TrendingUp, TrendingDown, Globe } from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { fmtPct } from '@/components/coveredCall/ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Index for the 14 sector income pages — /dividend-stocks-by-sector */
export default function SectorIncomeIndex() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/sectors`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useSeoMeta({
    title: 'Dividend stocks by sector — yields and payer counts for all 11 sectors | DiviDrip',
    description: 'Browse every dividend-paying company by sector: technology, utilities, REITs, energy, banks, insurance and more. Median yields, payer counts and monthly payers, updated nightly.',
    canonical: 'https://twylightcrow.com/dividend-stocks-by-sector',
  });

  const sectors = data?.sectors || [];
  const financial = sectors.filter((s) => s.parent === 'Financial Services');
  const rest = sectors.filter((s) => s.parent !== 'Financial Services');
  const maxYield = Math.max(...sectors.map((s) => s.median_yield || 0), 0.01);
  const industries = (data?.industries || []).filter((i) => i.count > 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="sector-index-page">
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 font-semibold">
        <Layers className="w-3.5 h-3.5" /> Sector income research
      </div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        Dividend stocks by <span className="text-cyan-600 dark:text-cyan-400">sector</span>
      </h1>
      <p className="mt-4 max-w-3xl text-base text-slate-600 dark:text-slate-400 leading-relaxed">
        {data ? `${data.total_stocks.toLocaleString()} dividend-paying companies` : 'Every dividend-paying company'},
        sorted into the sectors that actually behave differently. Each page ranks the whole sector by
        yield, payout ratio, dividend growth and our quality score — and calls out the highest
        yielders, the safest payouts and the fastest growers. Operating companies only: no ETFs,
        no closed-end funds, no ADRs.
      </p>

      {!data && (
        <div className="mt-8 grid sm:grid-cols-2 gap-3 animate-pulse" data-testid="sector-index-loading">
          {[...Array(8)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-slate-200 dark:bg-slate-800" />)}
        </div>
      )}

      {!!sectors.length && (
        <section className="mt-10" data-testid="sector-yield-map">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
            Where the yield is — and where it went
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Median trailing yield per sector today, with the change over the last 12 months.
            Recomputed from actual payout history and prices at each date, not a stored snapshot.
          </p>
          <div className="mt-4 space-y-1.5">
            {[...sectors].sort((a, b) => (b.median_yield || 0) - (a.median_yield || 0)).map((s) => {
              const pct = maxYield ? ((s.median_yield || 0) / maxYield) * 100 : 0;
              const chg = s.yield_change_1y;
              return (
                <Link
                  key={s.slug}
                  to={`/${s.slug}`}
                  className="group flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
                  data-testid={`sector-yield-bar-${s.slug}`}
                >
                  {/* Mobile: label + numbers on their own line so the bar
                      gets the full width. Desktop: single row. */}
                  <span className="flex items-center justify-between gap-2 sm:hidden">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{s.label}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-bold tabular-nums text-slate-900 dark:text-slate-100">{fmtPct(s.median_yield)}</span>
                      <span className={`text-[10px] font-bold tabular-nums ${chg > 0 ? 'text-emerald-600 dark:text-emerald-400' : chg < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'}`}>
                        {chg == null ? '—' : `${chg > 0 ? '+' : ''}${chg.toFixed(2)}`}
                      </span>
                    </span>
                  </span>
                  <span className="hidden sm:block w-36 shrink-0 text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{s.label}</span>
                  <span className="flex-1 h-4 rounded-full bg-slate-100 dark:bg-slate-800/80 overflow-hidden min-w-0">
                    <span
                      className="block h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 group-hover:from-cyan-400 group-hover:to-emerald-400 transition-all duration-500"
                      style={{ width: `${Math.max(3, pct)}%` }}
                    />
                  </span>
                  <span className="hidden sm:block w-14 shrink-0 text-right text-xs font-bold tabular-nums text-slate-900 dark:text-slate-100">
                    {fmtPct(s.median_yield)}
                  </span>
                  <span className="hidden sm:flex w-24 shrink-0 items-center justify-end gap-1">
                    <YieldSparkline series={s.yield_trend} change={chg} width={44} height={16} testId={`sector-map-spark-${s.slug}`} />
                    <span className={`w-12 text-right text-[10px] font-bold tabular-nums ${chg > 0 ? 'text-emerald-600 dark:text-emerald-400' : chg < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'}`}>
                      {chg == null ? '—' : `${chg > 0 ? '+' : ''}${chg.toFixed(2)}`}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Sparklines run 12 months → 6 months → 3 months → today. A falling median yield usually
            means prices rose faster than dividends, not that companies cut.
          </p>
        </section>
      )}

      {!!rest.length && (
        <section className="mt-10" data-testid="sector-index-sectors">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">The ten sectors</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {rest.map((s) => <SectorCard key={s.slug} s={s} />)}
          </div>
        </section>
      )}

      {!!financial.length && (
        <section className="mt-12" data-testid="sector-index-financial">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Financial services, split four ways</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Financials are the biggest dividend-paying sector by count, and a bank has nothing in
            common with a BDC — so it gets four pages instead of one.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {financial.map((s) => <SectorCard key={s.slug} s={s} />)}
          </div>
        </section>
      )}

      {!!industries.length && (
        <section className="mt-12" data-testid="sector-index-industries">
          <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">Zoom in by industry</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Narrower cuts for the industries big enough to stand on their own.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {industries.map((i) => (
              <Link
                key={i.slug}
                to={`/${i.slug}`}
                className="group flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 transition-colors"
                data-testid={`sector-index-industry-${i.slug}`}
              >
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{i.label}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
                  {i.count} · {fmtPct(i.median_yield)}
                  <ArrowRight className="inline w-3.5 h-3.5 ml-1 text-slate-400 group-hover:text-cyan-500" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-12 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="sector-index-cta">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Or build your own cut</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="sector-index-cta-screener">
            <Search className="w-4 h-4" /> Open the screener
          </Link>
          <Link to="/foreign-dividend-stocks" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="sector-index-cta-foreign">
            <Globe className="w-4 h-4" /> Foreign & ADR payers
          </Link>
          <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="sector-index-cta-calc">
            <Calculator className="w-4 h-4" /> DRIP calculator
          </Link>
          <Link to="/covered-call-etfs" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="sector-index-cta-cc">
            <Layers className="w-4 h-4" /> Covered call ETFs
          </Link>
          <Link to="/money-market-fund-yields" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="sector-index-cta-mmf">
            <Layers className="w-4 h-4" /> Money market yields
          </Link>
        </div>
      </section>

      <SiteDisclaimer />
    </div>
  );
}

function SectorCard({ s }) {
  const chg = s.yield_change_1y;
  return (
    <Link
      to={`/${s.slug}`}
      className="group p-4 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
      data-testid={`sector-index-card-${s.slug}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-slate-900 dark:text-slate-100">{s.label}</span>
        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 transition-transform group-hover:translate-x-0.5" />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400 min-w-0">
          <span><strong className="text-slate-700 dark:text-slate-300">{s.count}</strong> payers</span>
          <span>median <strong className="text-emerald-600 dark:text-emerald-400">{fmtPct(s.median_yield)}</strong></span>
          {chg != null && (
            <span
              className={`inline-flex items-center gap-0.5 font-semibold ${chg > 0 ? 'text-emerald-600 dark:text-emerald-400' : chg < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}
              title="Change in the sector's median trailing yield over the last 12 months"
            >
              {chg > 0 ? <TrendingUp className="w-3 h-3" /> : chg < 0 ? <TrendingDown className="w-3 h-3" /> : null}
              {chg > 0 ? '+' : ''}{chg.toFixed(2)} pts
            </span>
          )}
        </div>
        <YieldSparkline series={s.yield_trend} change={chg} testId={`sector-spark-${s.slug}`} />
      </div>
    </Link>
  );
}

/** 4-point median-yield line: 12m → 6m → 3m → now. */
export function YieldSparkline({ series, change, width = 64, height = 24, testId }) {
  const pts = (series || []).filter((p) => p.median_yield != null);
  if (pts.length < 2) return null;
  const vals = pts.map((p) => p.median_yield);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const step = width / (pts.length - 1);
  const coords = vals.map((v, i) => [i * step, height - ((v - min) / span) * (height - 4) - 2]);
  const d = coords.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const stroke = change > 0 ? '#059669' : change < 0 ? '#e11d48' : '#0891b2';
  const last = coords[coords.length - 1];
  return (
    <svg width={width} height={height} className="shrink-0 overflow-visible" data-testid={testId} aria-hidden="true">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.25" fill={stroke} />
    </svg>
  );
}
